
import React, { useState, useEffect } from 'react';
import { ArrowLeft, FileText, DollarSign, Calendar, Check, ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { formatHoursMinutes, formatTime } from '@/utils/timeUtils';
import { getUserPinOrThrow, shouldUsePinMode } from '@/utils/pinModeUtils';
// Production uniquement: pas de mode migration ici

interface TimeEntry {
  id: string;
  user_id: string;
  project_id: string;
  start_time: string;
  end_time: string | null;
  duration_minutes: number | null;
  description: string | null;
  is_billable: boolean;
  hourly_rate: number | null;
  is_invoiced?: boolean | null;
}

interface Project {
  id: string;
  name: string;
  client_name: string | null;
  hourly_rate: number | null;
  status?: 'active' | 'completed' | 'on-hold' | string | null;
}

interface Profile {
  id: string;
  first_name: string | null;
  last_name: string | null;
}

type BillingEntry = TimeEntry & { profile: Profile; _minutes: number; _amount: number };

interface ProjectBillingWeek {
  weekKey: string;
  weekStart: string;
  weekEnd: string;
  entries: BillingEntry[];
  totalMinutes: number;
  totalAmount: number;
  allInvoiced: boolean;
}

interface ProjectBilling {
  project: Project;
  weeks: ProjectBillingWeek[];
  totalMinutes: number;
  totalAmount: number;
  allInvoiced: boolean;
}

export const BillingView: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [billingData, setBillingData] = useState<ProjectBilling[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'unbilled' | 'billed'>('unbilled');
  const [selectedProjectId, setSelectedProjectId] = useState<string>('all');
  const [showCompletedProjects, setShowCompletedProjects] = useState(false);
  const [expandedProjectIds, setExpandedProjectIds] = useState<Record<string, boolean>>({});
  const { toast } = useToast();

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD' }).format(value);

  const computeMinutes = (e: { duration_minutes: number | null; start_time: string; end_time: string | null }) => {
    if (typeof e.duration_minutes === 'number' && !Number.isNaN(e.duration_minutes)) {
      return e.duration_minutes;
    }
    if (e.end_time) {
      const diff = (new Date(e.end_time).getTime() - new Date(e.start_time).getTime()) / 60000;
      return Math.max(0, Math.round(diff));
    }
    return 0;
  };

  useEffect(() => {
    fetchBillingData();
  }, []);

  const fetchBillingData = async () => {
    try {
      setLoading(true);
      const authMethod = typeof window !== 'undefined' ? window.localStorage.getItem('auth_method') : null;
      const managerPin = typeof window !== 'undefined' ? window.localStorage.getItem('manager_pin') : null;
      const usePinMode = authMethod === 'pin' && Boolean(managerPin);
      
      // Tables de production
      const timeEntriesTable = 'time_entries';
      const projectsTable = 'projects';
      const profilesTable = 'profiles';

      // 1) Récupérer les entrées de temps facturables (toutes, facturées et non facturées)
      const rangeStart = new Date();
      rangeStart.setFullYear(rangeStart.getFullYear() - 6); // couvre les imports historiques (ex: 2022-2025)
      rangeStart.setHours(0, 0, 0, 0);
      const rangeEnd = new Date();
      rangeEnd.setHours(23, 59, 59, 999);

      // NOTE: PostgREST limite souvent les résultats (ex: 1000 lignes).
      // En mode PIN, on pagine pour récupérer toute la plage.
      const fetchPinEntriesPaged = async () => {
        const all: any[] = [];
        const pageSize = 1000;
        let offset = 0;
        const maxPages = 50; // garde-fou
        for (let page = 0; page < maxPages; page++) {
          const { data, error } = await supabase.rpc('pin_list_time_entries_range', {
            p_pin: managerPin,
            p_start: rangeStart.toISOString(),
            p_end: rangeEnd.toISOString(),
            p_limit: pageSize,
            p_offset: offset,
            p_order: 'desc',
          });
          if (error) throw error;
          const rows = (data ?? []) as any[];
          all.push(...rows);
          if (rows.length < pageSize) break;
          offset += pageSize;
        }
        return all;
      };

      const { data: timeEntriesData, error: timeEntriesError } = usePinMode
        ? { data: await fetchPinEntriesPaged(), error: null as any }
        : await supabase
            .from(timeEntriesTable)
            .select('*')
            .eq('is_billable', true)
            .not('end_time', 'is', null)
            .order('start_time', { ascending: false });

      if (timeEntriesError) throw timeEntriesError;

      const rawEntries = (timeEntriesData || []) as any[];
      const entries = rawEntries
        .filter((e: any) => e && e.end_time) // requis pour la facturation
        .filter((e: any) => {
          // En mode PIN, la fonction peut ne pas retourner is_billable; dans ce cas on suppose facturable.
          if (usePinMode && typeof e.is_billable === 'undefined') return true;
          return Boolean(e.is_billable);
        });

      // 2) Hydrater projets et profils en requêtes séparées
      const uniqueProjectIds = Array.from(new Set(entries.map((e: any) => e.project_id)));
      const uniqueUserIds = Array.from(new Set(entries.map((e: any) => e.user_id)));

      const [projectsRes, profilesRes] = await Promise.all([
        usePinMode
          ? supabase.rpc('pin_list_projects', { p_pin: managerPin })
          : uniqueProjectIds.length > 0
              ? supabase.from(projectsTable).select('id, name, client_name, hourly_rate, status').in('id', uniqueProjectIds)
              : Promise.resolve({ data: [], error: null } as any),
        usePinMode
          ? supabase.rpc('pin_list_profiles', { p_pin: managerPin })
          : uniqueUserIds.length > 0
              ? supabase.from(profilesTable).select('id, first_name, last_name').in('id', uniqueUserIds)
              : Promise.resolve({ data: [], error: null } as any),
      ]);

      if (projectsRes.error) throw projectsRes.error;
      if (profilesRes.error) throw profilesRes.error;

      const projectMap = new Map<string, Project>((projectsRes.data || []).map((p: any) => [
        p.id,
        {
          id: p.id,
          name: p.name,
          client_name: p.client_name ?? null,
          hourly_rate: p.hourly_rate ?? null,
          status: p.status ?? 'active',
        },
      ]));
      const profileMap = new Map<string, Profile>((profilesRes.data || []).map((pr: any) => [pr.id, {
        id: pr.id,
        first_name: pr.first_name,
        last_name: pr.last_name,
      }]));

      // Liste des projets pour le filtre dérivée des entrées (comportement initial)

      // Grouping "projet -> semaines -> entrées" (vue par projet par défaut)
      const projectBuckets: Record<
        string,
        { project: Project; weeks: Record<string, Omit<ProjectBillingWeek, 'allInvoiced'>> }
      > = {};

      entries.forEach((entry: any) => {
        const projectId = entry.project_id;
        const project = projectMap.get(projectId) || { id: projectId, name: 'Projet', client_name: null, hourly_rate: 0, status: 'active' };

        const startDate = new Date(entry.start_time);
        const weekStart = getWeekStart(startDate);
        const weekEnd = getWeekEnd(startDate);
        const weekKey = weekStart.toISOString().split('T')[0];

        if (!projectBuckets[projectId]) {
          projectBuckets[projectId] = { project, weeks: {} };
        }

        if (!projectBuckets[projectId].weeks[weekKey]) {
          projectBuckets[projectId].weeks[weekKey] = {
            weekKey,
            weekStart: weekStart.toISOString(),
            weekEnd: weekEnd.toISOString(),
            entries: [],
            totalMinutes: 0,
            totalAmount: 0,
          };
        }

        const minutes = computeMinutes(entry);
        const hourlyRate = entry.hourly_rate || project.hourly_rate || 0;
        const amount = (minutes / 60) * hourlyRate;
        const billingEntry: BillingEntry = {
          ...entry,
          profile: profileMap.get(entry.user_id) || { id: entry.user_id, first_name: '', last_name: '' },
          _minutes: minutes,
          _amount: amount,
        };

        projectBuckets[projectId].weeks[weekKey].entries.push(billingEntry);
        projectBuckets[projectId].weeks[weekKey].totalMinutes += minutes;
        projectBuckets[projectId].weeks[weekKey].totalAmount += amount;
      });

      const projectData: ProjectBilling[] = Object.values(projectBuckets)
        .map((bucket) => {
          const weeks: ProjectBillingWeek[] = Object.values(bucket.weeks)
            .map((w) => ({
              ...w,
              allInvoiced: w.entries.length > 0 && w.entries.every((e) => Boolean(e.is_invoiced)),
            }))
            .sort((a, b) => new Date(b.weekStart).getTime() - new Date(a.weekStart).getTime());

          const totalMinutes = weeks.reduce((sum, w) => sum + w.totalMinutes, 0);
          const totalAmount = weeks.reduce((sum, w) => sum + w.totalAmount, 0);
          const allInvoiced = weeks.length > 0 && weeks.every((w) => w.entries.length > 0 && w.entries.every((e) => Boolean(e.is_invoiced)));

          return {
            project: bucket.project,
            weeks,
            totalMinutes,
            totalAmount,
            allInvoiced,
          };
        })
        .sort((a, b) => a.project.name.localeCompare(b.project.name));

      setBillingData(projectData);
    } catch (error) {
      console.error('Erreur lors du chargement des données de facturation:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les données de facturation",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getFilteredEntries = (entries: BillingEntry[]) => {
    if (filter === 'billed') {
      return entries.filter((e) => Boolean(e.is_invoiced));
    }
    if (filter === 'unbilled') {
      return entries.filter((e) => !e.is_invoiced);
    }
    return entries;
  };

  const markEntryAsInvoiced = async (entryId: string) => {
    try {
      if (shouldUsePinMode()) {
        const pin = getUserPinOrThrow();
        const { error } = await supabase.rpc('pin_mark_time_entry_invoiced', {
          p_pin: pin,
          p_entry_id: entryId,
        });
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('time_entries')
          .update({ is_invoiced: true, updated_at: new Date().toISOString() })
          .eq('id', entryId);
        if (error) throw error;
      }

      setBillingData((prev) =>
        prev.map((p) => {
          const updatedWeeks = p.weeks.map((w) => {
            const updatedEntries = w.entries.map((e) => (e.id === entryId ? { ...e, is_invoiced: true } : e));
            return {
              ...w,
              entries: updatedEntries,
              allInvoiced: updatedEntries.length > 0 && updatedEntries.every((e) => Boolean(e.is_invoiced)),
            };
          });
          return {
            ...p,
            weeks: updatedWeeks,
            allInvoiced: updatedWeeks.length > 0 && updatedWeeks.every((w) => w.entries.length > 0 && w.entries.every((e) => Boolean(e.is_invoiced))),
          };
        })
      );

      toast({ title: 'Succès', description: "Entrée marquée comme facturée" });
    } catch (err: any) {
      console.error(err);
      toast({ title: 'Erreur', description: err?.message || "Impossible de marquer cette entrée", variant: 'destructive' });
    }
  };

  const getWeekStart = (date: Date) => {
    const start = new Date(date);
    const day = start.getDay();
    const diff = start.getDate() - day + (day === 0 ? -6 : 1);
    start.setDate(diff);
    start.setHours(0, 0, 0, 0);
    return start;
  };

  const getWeekEnd = (date: Date) => {
    const end = getWeekStart(date);
    end.setDate(end.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return end;
  };

  const markWeekAsInvoiced = async (projectId: string, weekKey: string) => {
    try {
      const projectData = billingData.find((p) => p.project.id === projectId);
      const week = projectData?.weeks.find((w) => w.weekKey === weekKey);
      if (!week) return;

      // Mise à jour BD : marquer uniquement les entrées non facturées de la semaine + projet comme facturées
      const ids = week.entries.filter((e) => !e.is_invoiced).map((e) => e.id);
      if (ids.length === 0) {
        toast({ title: 'Info', description: 'Toutes les entrées sont déjà facturées.' });
        return;
      }
      if (shouldUsePinMode()) {
        const pin = getUserPinOrThrow();
        const { error: updateError } = await supabase.rpc('pin_mark_time_entries_invoiced', {
          p_pin: pin,
          p_entry_ids: ids,
        });
        if (updateError) throw updateError;
      } else {
        const { error: updateError } = await supabase
          .from('time_entries')
          .update({ is_invoiced: true, updated_at: new Date().toISOString() })
          .in('id', ids);
        if (updateError) throw updateError;
      }
      toast({
        title: "Succès",
        description: `${ids.length} entrées marquées comme facturées`,
      });

      // Update local state
      setBillingData((prev) =>
        prev.map((p) => {
          if (p.project.id !== projectId) return p;
          const updatedWeeks = p.weeks.map((w) => {
            if (w.weekKey !== weekKey) return w;
            const updatedEntries = w.entries.map((e) => (ids.includes(e.id) ? { ...e, is_invoiced: true } : e));
            return {
              ...w,
              entries: updatedEntries,
              allInvoiced: updatedEntries.length > 0 && updatedEntries.every((e) => Boolean(e.is_invoiced)),
            };
          });
          return {
            ...p,
            weeks: updatedWeeks,
            allInvoiced: updatedWeeks.length > 0 && updatedWeeks.every((w) => w.entries.length > 0 && w.entries.every((e) => Boolean(e.is_invoiced))),
          };
        })
      );
    } catch (error: any) {
      console.error('Erreur lors de la mise à jour:', error);
      toast({
        title: "Erreur",
        description: error?.message || "Impossible de marquer comme facturé",
        variant: "destructive",
      });
    }
  };

  const markProjectAsInvoiced = async (projectId: string) => {
    try {
      const projectData = billingData.find((p) => p.project.id === projectId);
      if (!projectData) return;

      const ids = projectData.weeks.flatMap((w) => w.entries.filter((e) => !e.is_invoiced).map((e) => e.id));
      if (ids.length === 0) {
        toast({ title: 'Info', description: 'Toutes les entrées sont déjà facturées.' });
        return;
      }

      if (shouldUsePinMode()) {
        const pin = getUserPinOrThrow();
        const { error: updateError } = await supabase.rpc('pin_mark_time_entries_invoiced', {
          p_pin: pin,
          p_entry_ids: ids,
        });
        if (updateError) throw updateError;
      } else {
        const { error: updateError } = await supabase
          .from('time_entries')
          .update({ is_invoiced: true, updated_at: new Date().toISOString() })
          .in('id', ids);
        if (updateError) throw updateError;
      }

      toast({
        title: 'Succès',
        description: `${ids.length} entrées marquées comme facturées`,
      });

      setBillingData((prev) =>
        prev.map((p) => {
          if (p.project.id !== projectId) return p;
          const updatedWeeks = p.weeks.map((w) => {
            const updatedEntries = w.entries.map((e) => (ids.includes(e.id) ? { ...e, is_invoiced: true } : e));
            return {
              ...w,
              entries: updatedEntries,
              allInvoiced: updatedEntries.length > 0 && updatedEntries.every((e) => Boolean(e.is_invoiced)),
            };
          });
          return {
            ...p,
            weeks: updatedWeeks,
            allInvoiced: updatedWeeks.length > 0 && updatedWeeks.every((w) => w.entries.length > 0 && w.entries.every((e) => Boolean(e.is_invoiced))),
          };
        })
      );
    } catch (error: any) {
      console.error('Erreur lors de la mise à jour:', error);
      toast({
        title: 'Erreur',
        description: error?.message || 'Impossible de marquer comme facturé',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 p-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center py-8">
            <div className="text-lg">Chargement des données de facturation...</div>
          </div>
        </div>
      </div>
    );
  }

  const toggleProjectExpanded = (projectId: string) => {
    setExpandedProjectIds((prev) => ({ ...prev, [projectId]: !prev[projectId] }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 p-4">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="icon" onClick={onBack}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Facturation</h1>
              <p className="text-muted-foreground">Gestion des heures facturables</p>
            </div>
          </div>
        </div>

        {/* Filtre facturation */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Filtrer les entrées</Label>
                  <RadioGroup
                    value={filter}
                    onValueChange={(v) => setFilter(v as 'all' | 'unbilled' | 'billed')}
                    className="grid grid-cols-3 gap-4 md:w-auto"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="all" id="filter-all" />
                      <Label htmlFor="filter-all">Tous</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="unbilled" id="filter-unbilled" />
                      <Label htmlFor="filter-unbilled">Non facturés</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="billed" id="filter-billed" />
                      <Label htmlFor="filter-billed">Facturés</Label>
                    </div>
                  </RadioGroup>
                </div>

                <div className="space-y-2">
                  <Label>Filtrer par projet</Label>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">
                      Afficher aussi les projets terminés
                    </span>
                    <Switch
                      checked={showCompletedProjects}
                      onCheckedChange={setShowCompletedProjects}
                    />
                  </div>
                  <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Tous les projets" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les projets</SelectItem>
                      {billingData
                        .map((p) => p.project)
                        .filter((project) => showCompletedProjects || project.status !== 'completed')
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((project) => (
                          <SelectItem key={project.id} value={project.id}>
                            {project.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Vue Facturation par projet */}
        {billingData
          .filter((p) => (selectedProjectId === 'all' ? true : p.project.id === selectedProjectId))
          .filter((p) => (showCompletedProjects ? true : p.project.status !== 'completed'))
          .map((p) => {
            const visibleWeeks = p.weeks
              .map((w) => {
                const visibleEntries = getFilteredEntries(w.entries);
                const visibleMinutes = visibleEntries.reduce((sum, e) => sum + e._minutes, 0);
                const visibleAmount = visibleEntries.reduce((sum, e) => sum + e._amount, 0);
                const allInvoiced = visibleEntries.length > 0 && visibleEntries.every((e) => Boolean(e.is_invoiced));
                const unbilledIds = w.entries.filter((e) => !e.is_invoiced).map((e) => e.id);
                return {
                  ...w,
                  visibleEntries,
                  visibleMinutes,
                  visibleAmount,
                  visibleAllInvoiced: allInvoiced,
                  unbilledIds,
                };
              })
              .filter((w) => w.visibleEntries.length > 0);

            if (visibleWeeks.length === 0) return null;

            const projectVisibleMinutes = visibleWeeks.reduce((sum, w) => sum + w.visibleMinutes, 0);
            const projectVisibleAmount = visibleWeeks.reduce((sum, w) => sum + w.visibleAmount, 0);
            const projectVisibleEntries = visibleWeeks.flatMap((w) => w.visibleEntries);
            const hasAnyInvoiced = projectVisibleEntries.some((e) => Boolean(e.is_invoiced));
            const allInvoiced = projectVisibleEntries.length > 0 && projectVisibleEntries.every((e) => Boolean(e.is_invoiced));
            const projectUnbilledIds = p.weeks.flatMap((w) => w.entries.filter((e) => !e.is_invoiced).map((e) => e.id));
            const statusLabel = allInvoiced ? 'Tout facturé' : hasAnyInvoiced ? 'Partiel' : 'Non facturé';

            const expanded = Boolean(expandedProjectIds[p.project.id]);

            return (
              <Card key={p.project.id} className="overflow-hidden">
                <CardContent className="p-0">
                  <button
                    type="button"
                    className="w-full text-left p-4 hover:bg-muted/40 transition-colors"
                    onClick={() => toggleProjectExpanded(p.project.id)}
                    aria-expanded={expanded}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 min-w-0">
                        <span className="mt-1 text-muted-foreground">
                          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </span>
                        <div className="min-w-0">
                          <div className="font-semibold truncate">{p.project.name}</div>
                          <div className="text-sm text-muted-foreground truncate">
                            {p.project.client_name ? `Client : ${p.project.client_name}` : 'Client : —'}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 shrink-0">
                        <div className="text-right">
                          <div className="text-lg font-bold">{formatHoursMinutes(projectVisibleMinutes)}</div>
                          <div className="text-sm text-muted-foreground">{formatCurrency(projectVisibleAmount)}</div>
                        </div>

                        {filter !== 'billed' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(event) => {
                              event.stopPropagation();
                              markProjectAsInvoiced(p.project.id);
                            }}
                            disabled={projectUnbilledIds.length === 0}
                          >
                            <DollarSign className="h-4 w-4 mr-2" />
                            Facturer
                          </Button>
                        )}

                        <Badge
                          variant="secondary"
                          className={
                            allInvoiced
                              ? 'bg-green-100 text-green-800'
                              : hasAnyInvoiced
                                ? 'bg-amber-100 text-amber-900'
                                : 'bg-slate-100 text-slate-900'
                          }
                        >
                          {allInvoiced ? <Check className="h-3 w-3 mr-1" /> : null}
                          {statusLabel}
                        </Badge>
                      </div>
                    </div>
                  </button>

                  {expanded && (
                    <div className="border-t p-4 space-y-8">
                      {visibleWeeks.map((w) => (
                        <div key={w.weekKey} className="space-y-3">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <div className="flex items-center gap-2 font-semibold">
                                <Calendar className="h-4 w-4" />
                                Semaine du {new Date(w.weekStart).toLocaleDateString('fr-FR')} au{' '}
                                {new Date(w.weekEnd).toLocaleDateString('fr-FR')}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {formatHoursMinutes(w.visibleMinutes)} · {formatCurrency(w.visibleAmount)}
                              </div>
                            </div>

                            <div className="flex items-center gap-2">
                              {w.visibleAllInvoiced ? (
                                <Badge variant="secondary" className="bg-green-100 text-green-800">
                                  <Check className="h-3 w-3 mr-1" />
                                  Facturé
                                </Badge>
                              ) : filter === 'billed' ? null : (
                                <Button
                                  size="sm"
                                  onClick={() => markWeekAsInvoiced(p.project.id, w.weekKey)}
                                  disabled={w.unbilledIds.length === 0}
                                >
                                  <DollarSign className="h-4 w-4 mr-2" />
                                  Marquer la semaine comme facturée
                                </Button>
                              )}
                            </div>
                          </div>

                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Employé</TableHead>
                                <TableHead>Date</TableHead>
                                <TableHead>Durée</TableHead>
                                <TableHead>Description</TableHead>
                                <TableHead className="text-right">Facturation</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {w.visibleEntries.map((entry) => (
                                <TableRow key={entry.id}>
                                  <TableCell>
                                    {entry.profile.first_name} {entry.profile.last_name}
                                  </TableCell>
                                  <TableCell>{new Date(entry.start_time).toLocaleDateString('fr-FR')}</TableCell>
                                  <TableCell>{formatHoursMinutes(entry._minutes)}</TableCell>
                                  <TableCell className="max-w-xs truncate">{entry.description || '-'}</TableCell>
                                  <TableCell className="text-right">
                                    {entry.is_invoiced ? (
                                      <Badge variant="secondary" className="bg-green-100 text-green-800">
                                        <Check className="h-3 w-3 mr-1" />
                                        Facturé
                                      </Badge>
                                    ) : (
                                      <Button size="sm" variant="outline" onClick={() => markEntryAsInvoiced(entry.id)}>
                                        <DollarSign className="h-4 w-4 mr-2" />
                                        Facturer
                                      </Button>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}

        {billingData.length === 0 ||
        billingData
          .filter((p) => (selectedProjectId === 'all' ? true : p.project.id === selectedProjectId))
          .filter((p) => (showCompletedProjects ? true : p.project.status !== 'completed'))
          .every((p) => p.weeks.every((w) => getFilteredEntries(w.entries).length === 0)) ? (
          <Card>
            <CardContent className="text-center py-8">
              <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">Aucune donnée de facturation</h3>
              <p className="text-muted-foreground">
                Il n'y a pas encore d'heures facturables enregistrées.
              </p>
            </CardContent>
          </Card>
        ) : null}
      </div>
    </div>
  );
};
